import { ChildProcess, fork } from 'child_process'
import * as fs from 'fs'
import { prompt } from 'inquirer'
import * as yaml from 'js-yaml'
import * as path from 'path'
import { Writable } from 'stream'
import { envDefinitions, MainOperator } from 'yam-operator'
import { AppCatalog } from 'yam-operator/catalog.d'
import { DEFAULT_KUBE_CONFIG, VALUES_DIR } from 'yam-operator/constants'
import YamPluginComposer from 'yam-operator/engine/composer'
import * as catalog from 'yam-operator/schema/catalog_schema.json'
import TemplateRender, { VariableDefinition } from 'yam-operator/template/render'
import { EngineConfig, KubernetesEnvironment } from 'yam-operator/types.d'
import { createLogger, fsExist, fsMkdir, fsReadDir, fsWrite, lookupWildcardKeyObj, parseBaseType, splitAndTrim, splitAndTrimComma, trimAndLowerAndRemoveSlash } from 'yam-operator/util'
import { IApplicationModel, KV, OperatorMode } from 'yam-types'
import { DEFAULT_APP_FILE_NAME, DEFAULT_APP_YAML, DEFAULT_CHECK_STATUS_SECONDS, DEFAULT_ENGINE_CONFIG, ENGINE_BASE_PATH } from '../constants'
import builtInPlugins from '../built-in-plugins'

const log = createLogger('cli')
const ENV_PLACEHOLDER_PREFIX = '${env.'
const ENV_PLACEHOLDER_SUFFIX = '}'

if (process.env.NODE_OPTIONS) {
  log.info(`starting cli with options: ${process.env.NODE_OPTIONS}`)
}

export let engineSettings = DEFAULT_ENGINE_CONFIG

let engineOperator: MainOperator

let confFile = path.join(ENGINE_BASE_PATH, 'config.yaml')

export async function initEngine(): Promise<MainOperator> {
  const envSettingLocation = process.env.YAM_CONFIG
  if (envSettingLocation) {
    if (!fs.existsSync(envSettingLocation)) {
      throw new Error(`settings file specified in $YAM_CONFIG is not found: '${envSettingLocation}'`)
    }
    confFile = envSettingLocation
  }

  if (fs.existsSync(confFile)) {
    const cfg = fs.readFileSync(confFile).toString().trim()
    if (cfg) {
      engineSettings = yaml.load(cfg) as EngineConfig
    } else {
      engineSettings.plugins = (await builtInPlugins()).map(p => ({
        // built-in plugins don't have specific version, depend on cli version
        version: 'latest',
        name: p.name,
        builtIn: true,
        enable: true,
        settings: {}
      }))
      persistSettings()
    }
  } else {
    log.debug(`init default settings and write into '~/.yam/config.yaml' at first time`)
    persistSettings()
  }
  replaceEnvironmentVariables(engineSettings as unknown as KV)
  engineOperator = new MainOperator(engineSettings, await builtInPlugins())
  return engineOperator
}

function replaceEnvironmentVariables(obj: KV) {
  for (const key in obj) {
    const tmpVal = obj[key]
    if (!tmpVal) {
      continue
    }
    if (typeof tmpVal === "string" && tmpVal.indexOf(ENV_PLACEHOLDER_PREFIX) !== -1) {
      const idx = tmpVal.indexOf(ENV_PLACEHOLDER_PREFIX)
      const prefixVal = tmpVal.substring(0, idx)
      const endIdx = tmpVal.substring(idx).indexOf(ENV_PLACEHOLDER_SUFFIX)
      if (endIdx !== -1) {
        const envParam = tmpVal.substring(idx + ENV_PLACEHOLDER_PREFIX.length, idx + endIdx)
        obj[key] = prefixVal + (process.env[envParam] || '') + tmpVal.substring(idx + endIdx + 1)
        log.trace(`replacing env var ${envParam} with value: ${process.env[envParam] || ''}`)
      }
    } else if (typeof tmpVal === "object") {
      replaceEnvironmentVariables(tmpVal as KV)
    }
  }
}

export async function planApply(runMode: OperatorMode, options: PlanApplyOption, throwErr?: boolean, uninstallApp?: string, additionalValues?: string): Promise<boolean> {
  const clusters: KubernetesEnvironment[] = []
  const kubeConf = engineSettings.clientConf.kubeConfig || DEFAULT_KUBE_CONFIG()
  let adHocKubeConf = ''
  if (process.env.KUBECONFIG && process.env.KUBECONFIG.indexOf('kind: Config') !== -1) {
    log.debug(`got higher priority ad-hoc kubeConfig from environment`)
    adHocKubeConf = process.env.KUBECONFIG as string
  }
  if (runMode !== "apply-only") {
    if (!options.clusters) {
      const { fetched, cluster } = await promptDefaultCluster()
      if (!fetched) {
        return false
      }
      if (cluster) {
        clusters.push(cluster)
      }
    } else {
      const ctxMap = engineSettings.clientConf.contextPatternMapping
      const kubeFileMap = engineSettings.clientConf.kubeConfigFilePatternMapping
      options.clusters.split(',').map(c => c.trim()).filter(c => !!c).forEach(c => {
        const ctxParts = splitAndTrim(c)
        const name = trimAndLowerAndRemoveSlash(c)
        clusters.push({
          name,
          stack: ctxParts[0],
          envLocators: ctxParts,
          kubeConfContextName: lookupWildcardKeyObj(name, ctxMap) as string || c,
          kubeConfPath: adHocKubeConf || lookupWildcardKeyObj(name, kubeFileMap) as string || kubeConf,
          rawClusterName: c
        })
      })
    }
  }

  const cmdParams = new Map<string, string>()
  if (options.version) {
    if (options.version as unknown as boolean === true) {
      log.info(`app version is not set in current operation`)
    } else {
      cmdParams.set('version', options.version)
    }
  }
  if (options.set && options.set.split) {
    options.set.split(',')
      .map(r => r.trim().split('='))
      .filter(arr => arr.length === 2)
      .forEach(arr => {
        cmdParams.set(arr[0].trim(), arr[1].trim())
      })
  }
  try {
    let waitSeconds = DEFAULT_CHECK_STATUS_SECONDS
    if (typeof options.waitSeconds !== "boolean") {
      const num = parseInt(options.waitSeconds as string)
      if (!isNaN(num)) {
        waitSeconds = num
      }
    } else {
      waitSeconds = options.waitSeconds ? DEFAULT_CHECK_STATUS_SECONDS : 0
    }

    // normalize extra values files and dir
    additionalValues = additionalValues || ''
    if (options.valuesFrom && typeof options.valuesFrom !== "boolean") {
      additionalValues = ',' + options.valuesFrom
    }
    const valueSources = additionalValues.split(',').filter(x => x)
    await engineOperator.operate({
      workingDir: options.dir || './',
      runMode,
      cmdParams,
      clusters,
      templateOutput: options.templateOutput || false,
      statusWaitSeconds: waitSeconds,
      dryRunMode: parseBaseType(options.dryRun) as boolean || false,
      forceRestartWorkload: parseBaseType(options.forceRestart) as boolean || false,
      engine: engineSettings,
      noDiff: parseBaseType(options.skipDiff) as boolean || false,
      planFile: options.planFile,
      uninstallApp,
      valueSources,
      filteredPlugins: splitAndTrimComma(options.filter),
    })
    return true
  } catch (ex) {
    log.error(`Failed: ${ex.message}. ${ex.statusCode || ''}. ${ex.body ? ('\n' + JSON.stringify(ex.body)) : ''}`)
    if (log.isLevelEnabled("debug")) {
      log.error(ex.stack)
    }
    if (throwErr) {
      throw ex
    } else {
      return false
    }
  }
}

export async function promptDefaultCluster(): Promise<{ fetched: boolean, cluster?: KubernetesEnvironment }> {
  const kubeConf = engineSettings.clientConf.kubeConfig || DEFAULT_KUBE_CONFIG()
  if (fs.existsSync(kubeConf)) {
    const currentContext = ((yaml.load(fs.readFileSync(kubeConf).toString()) || {}) as KV)['current-context'] as string
    if (!currentContext) {
      log.info(`current-context is not found in ${kubeConf}, operation aborted.`)
      return { fetched: false }
    }
    const answer = await prompt({
      name: 'confirmCtx', type: "confirm",
      message: `would you like to use kubeconfig file in ${kubeConf}, and operate in Kubernetes context: '${currentContext}' ?`
    })
    if (!answer?.confirmCtx) {
      log.info(`user cancelled, operation aborted.`)
      return { fetched: false }
    }
    // use current context as kubernetes env
    const ctxParts = splitAndTrim(currentContext)
    return {
      fetched: true,
      cluster: {
        name: trimAndLowerAndRemoveSlash(currentContext),
        stack: ctxParts[0],
        envLocators: ctxParts,
        kubeConfContextName: currentContext,
        kubeConfPath: kubeConf,

        // when running in plan file mode, make sure the original data passed through
        rawClusterName: currentContext
      }
    }
  } else {
    log.error(`'-c, --clusters' option is not specified and kubeconfig is not found in '${kubeConf}'`)
    return { fetched: false }
  }
}

export async function applyAppBundle(bundleFile: string, options: PlanApplyOption, throwErr?: boolean): Promise<boolean> {
  if (!fs.existsSync(bundleFile)) {
    log.error(`bundle file '${bundleFile}' is not found`)
    return false
  }
  const cwd = path.dirname(bundleFile)
  const catalog = yaml.load(fs.readFileSync(bundleFile).toString()) as AppCatalog
  const bundle = catalog?.apps
  // TODO support remote app bundle file with URL
  const valid = bundle instanceof Array && bundle.every(app => app && app.dir && app.name && fs.existsSync(path.join(cwd, app.dir)))
  if (valid) {
    try {
      log.info(`sync YAM bundle files: ${bundleFile} into env: ${options.clusters}`)
      let inclusiveMode = false
      let exclusiveMode = false
      let included: string[] = []
      let excluded: string[] = []
      if (options.include && options.include.trim()) {
        inclusiveMode = true
        included = splitAndTrimComma(options.include)
      }
      if (options.exclude && options.exclude.trim()) {
        exclusiveMode = true
        excluded = splitAndTrimComma(options.exclude)
      }
      const targetClusters = splitAndTrimComma(options.clusters)
      if (targetClusters.length === 0) {
        // add a dummy cluster to loop once and use default Kubernetes context
        targetClusters.push('')
      }
      for (const cls of targetClusters) {
        options.clusters = cls
        log.info(`# --------- Installing ${catalog.name} into: ${cls || '<default-context>'} ----------- #`)
        for (const app of bundle) {
          let shouldApply = true
          if (inclusiveMode) {
            shouldApply = included.includes(app.name)
          }
          if (exclusiveMode) {
            shouldApply = !excluded.includes(app.name)
          }
          if (!shouldApply) {
            log.info(`filter matched, skip apply app model for '${app.name}'`)
            continue
          }
          log.info(`--------------------- Apply '${app.name}' Start -------------------------\n`)
          options.dir = path.join(cwd, app.dir || '')
          await planApply("plan-apply", options, true, undefined, path.join(cwd, VALUES_DIR))
          log.info(`\n--------------------- Apply '${app.name}' End  ----------------------------\n\n`)
        }
        log.info(`# --------- Installed ${catalog.name} into: ${cls || '<default-context>'} ----------- #`)
      }
    } catch (ex) {
      log.error(`Apply app bundle failed: ${ex.message}. ${ex.statusCode || ''}. ${ex.body ? ('\n' + JSON.stringify(ex.body)) : ''}`)
      if (log.isLevelEnabled("debug")) {
        log.error(ex.stack)
      }
      if (throwErr) {
        throw ex
      } else {
        return false
      }
    }
  } else {
    log.error(`invalid app bundle file: ${bundleFile}`)
    if (throwErr) {
      throw new Error(`invalid app bundle file: ${bundleFile}`)
    } else {
      return false
    }
  }
  return true
}

export function validateWorkloadName(workload: string, dir?: string): boolean {
  if (!workload.match(/^[a-z-0-9]{1,256}\/[a-z-0-9]{1,256}$/g)) {
    log.error(`invalid workload name: ${workload}, must be lowercase-alphabet/numeric/hyphen chars, for example: 'my-team/my-app'`)
    return false
  }
  if (fs.existsSync(path.join(dir || process.cwd(), DEFAULT_APP_FILE_NAME))) {
    log.warn(`skipped generating 'app.yaml', file exists.`)
    return false
  }
  return true
}

export function generateModel(workload: string, dir?: string): void {
  if (!validateWorkloadName(workload, dir)) {
    return
  }
  const [namespace, app] = workload.split('/')
  const model = JSON.parse(JSON.stringify(DEFAULT_APP_YAML)) as IApplicationModel
  model.metadata.app = app
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const deploy = (model.deploy as any)
  deploy.containers[0].name = app
  model.metadata.namespace = namespace
  fs.writeFileSync(path.join(dir || process.cwd(), DEFAULT_APP_FILE_NAME), yaml.dump(model))
  const valuesDir = path.join(dir || process.cwd(), VALUES_DIR)
  if (!fs.existsSync(valuesDir)) {
    fs.mkdirSync(valuesDir)
  }
  const valuesExample = path.join(valuesDir, "params.yaml")
  if (!fs.existsSync(valuesExample)) {
    fs.writeFileSync(valuesExample, "# All parameters in any 'values file' could be used elsewhere, eg. ~{ example-dynamic-param }\n" + yaml.dump({
      "example-dynamic-param": {
        "dev": "example-value-1",
        "staging": "example-value-2",
        "prod": "example-value-3",
      }
    }))
  }
  log.info(`[YAM Engine] 'app.yaml' created for app '${app}', namespace is '${namespace}'`)
}

export async function generateAppAndSettingsSchema(): Promise<{ app: string, settings: string, catalog: string }> {
  const pluginComposer = new YamPluginComposer()
  for (const p of engineSettings.plugins) {
    if (p.builtIn) {
      const builtInPlugin = (await builtInPlugins()).find(x => x.name === p.name)
      if (builtInPlugin) {
        pluginComposer.builtInPlugins.push(builtInPlugin)
      }
    }
  }
  const res = await pluginComposer.compose(engineSettings.plugins)
  const finalSchema = JSON.parse(JSON.stringify(res.jsonSchema))
  const settingsSchema = JSON.parse(JSON.stringify(res.settingsSchema))
  const catalogSchema = JSON.parse(JSON.stringify(catalog))
  appendPlaceHolder(finalSchema)
  // FIX: set default to empty to avoid default property automatically set by ncc_require/import
  finalSchema.default = {}
  settingsSchema.default = {}
  catalogSchema.default = []
  return {
    app: JSON.stringify(finalSchema, undefined, 2),
    settings: JSON.stringify(settingsSchema, undefined, 2),
    catalog: JSON.stringify(catalogSchema, undefined, 2)
  }
}

export function appendPlaceHolder(obj: KV): void {
  if (obj.anyOf) {
    const options = obj.anyOf as KV[]
    options.forEach(option => {
      appendPlaceHolder(option)
    })
    if (!options.find(x => x.type === "string")) {
      options.push({
        "type": "string",
        "pattern": "~\\{.+\\}"
      })
    }
  }
  if (obj.properties) {
    const tmp = obj.properties as KV
    for (const key in tmp) {
      const type = (tmp[key] as KV).type
      if (type === "string") {
        continue
      }
      if (type === "array") {
        appendPlaceHolder((tmp[key] as KV).items as KV)
      }
      if (type === "object") {
        appendPlaceHolder(tmp[key] as KV)
      }
      (obj.properties as KV)[key] = {
        anyOf: [{
          "type": "string",
          "pattern": "~\\{.+\\}"
        }, tmp[key]]
      }
    }
  }
}

export async function listParameterValues(opts: ValuesOption): Promise<{ customizedValues: KV, varDefinitions: VariableDefinition[] }> {
  const render = new TemplateRender()
  if (!opts.env) {
    throw new Error('invalid param, environment locator is not specified')
  }
  const normalizedEnv = trimAndLowerAndRemoveSlash(opts.env)
  const workingDir = opts.dir || process.cwd()
  const envLocators = normalizedEnv.split('/')
  const valueSources = opts.valuesFrom?.split(',').filter(x => x)
  const appModel = await render.loadYaml(workingDir, 'app.yaml') as IApplicationModel

  const additionalGlobalValues = envDefinitions(engineSettings, engineSettings.envDefinitions).envGlobalValues

  const { values, varDefinitions, rawParameters } = await render.readCustomizedValues(
    workingDir, new Map(), [{ envLocators, name: normalizedEnv, stack: envLocators[0], kubeConfContextName: '-' }],
    appModel, engineSettings.envLevelDefinitions, valueSources, engineSettings.clientConf, additionalGlobalValues
  )
  const customizedValues = values.get(normalizedEnv)
  if (!customizedValues) {
    throw new Error(`env customized values not exists`)
  }
  varDefinitions.forEach((variable) => {
    if (variable.builtIn) {
      variable.merged = customizedValues[variable.name]
      return
    }
    const envKv = rawParameters.get(variable.name)
    if (!envKv) {
      return
    }
    variable.current = envKv[normalizedEnv]
    variable.merged = customizedValues[variable.name]
  })
  return { customizedValues, varDefinitions }
}

export async function getParameterValues(opts: ValuesOption): Promise<KV | undefined> {
  if (!opts.filename) {
    throw new Error('invalid param, filename is not specified')
  }
  const workingDir = opts.dir || process.cwd()
  const render = new TemplateRender()
  const obj = await render.loadYaml(workingDir, opts.filename) as KV
  const filterVar = opts.variables as string
  return filterVar ? { [filterVar]: obj[filterVar] } : obj
}

export async function setParameterValues(opts: ValuesOption | ValuesOption[]): Promise<void> {
  if (opts instanceof Array) {
    for (const opt of opts) {
      await setParameterValues(opt)
    }
    return
  }
  if (!opts.env || !opts.variables) {
    throw new Error(`invalid parameter, no env or filename or variables specified`)
  }
  if (typeof opts.variables !== "object") {
    throw new Error(`variables parameter is not Key-Value object`)
  }
  const writeFile = path.join(opts.filename || 'values/params.yaml')
  const normalizedEnv = trimAndLowerAndRemoveSlash(opts.env)
  const workingDir = opts.dir || process.cwd()
  const render = new TemplateRender()
  const fullPath = path.join(workingDir, writeFile)
  const dir = path.dirname(fullPath)
  const exists = await fsExist(dir)
  if (!exists) {
    await fsMkdir(dir)
  }
  let obj: { [key: string]: KV } = {}
  if (await fsExist(fullPath)) {
    obj = await render.loadYaml(workingDir, writeFile) as { [key: string]: KV }
  }
  for (const param in opts.variables) {
    const val = opts.variables[param]
    if (obj[param] === undefined) {
      obj[param] = {}
    }
    const prev = obj[param][normalizedEnv]
    obj[param][normalizedEnv] = val
    if (log.isLevelEnabled('debug')) {
      log.debug(`override param '${param}' with value: ${JSON.stringify(val)}. previous value is: ${JSON.stringify(prev)}`)
    }
    log.info(`write parameter '${param}' to envLocator '${normalizedEnv}'`)
  }
  await fsWrite(fullPath, yaml.dump(obj))
  log.info(`parameters has been written into '${writeFile}' for env '${normalizedEnv}'`)
}

export async function deleteParameterValues(opts: ValuesOption): Promise<void> {
  if (!opts.filename || !opts.variables) {
    throw new Error(`invalid parameter, no env or filename or variables specified`)
  }
  const deleteAllEnv = !opts.env
  const normalizedEnv = trimAndLowerAndRemoveSlash(opts.env || '')
  const workingDir = opts.dir || process.cwd()
  const fullPath = path.join(workingDir, opts.filename)
  const render = new TemplateRender()
  if (await fsExist(fullPath)) {
    const obj = await render.loadYaml(workingDir, opts.filename) as { [key: string]: KV }
    if (typeof opts.variables !== "string") {
      throw new Error(`the target file doesn't exists: '${opts.filename}'`)
    }
    opts.variables.split(',').map(x => x.trim()).filter(x => x).forEach(varName => {
      if (obj[varName] === undefined) {
        throw new Error(`parameter '${varName}' is not found in file: ${opts.filename}`)
      }
      if (deleteAllEnv) {
        delete obj[varName]
        log.info(`deleted '${varName}' in all environment locators, file: ${opts.filename}`)
      } else {
        delete obj[varName][normalizedEnv]
        log.info(`deleted '${varName}' in env locator '${normalizedEnv}', file: ${opts.filename}`)
      }
    })
    await fsWrite(fullPath, yaml.dump(obj))
    log.info(`finish deleting ${opts.variables} for ${deleteAllEnv ? 'all environments' : normalizedEnv}`)
  } else {
    throw new Error(`the target file doesn't exists: '${opts.filename}'`)
  }
}

export type PreviewResult = {
  file: string
  content: string
  missingKeys: string[]
}

export async function previewTemplate(opts: ValuesOption): Promise<PreviewResult[]> {
  if (!opts.env || !opts.filename) {
    throw new Error(`invalid parameter, no env or filename specified.`)
  }
  const workingDir = opts.dir || process.cwd()
  const { customizedValues } = await listParameterValues(opts)
  if (!customizedValues) {
    log.error(`can not found customized parameters to render the target file`)
  }
  const toRenderFiles = opts.filename.split(',').filter(x => x)
  const render = new TemplateRender()
  const result: PreviewResult[] = []
  for (const f of toRenderFiles) {
    const valDirOrFile = path.join(workingDir, f)
    if (fs.statSync(valDirOrFile).isDirectory()) {
      const files = await fsReadDir(valDirOrFile)
      for (const subFile of files) {
        const relativePath = path.join(f, subFile)
        if (fs.statSync(path.join(valDirOrFile, subFile)).isDirectory()) {
          log.warn(`recursive template rendering is not allowed, skip directory ${relativePath}`)
        } else {
          const missingKeys: string[] = []
          const content = await render.renderTemplate(workingDir, relativePath, false, customizedValues, missingKeys)
          result.push({ file: f + '/' + subFile, content, missingKeys })
        }
      }
    } else {
      const missingKeys: string[] = []
      const content = await render.renderTemplate(workingDir, f, false, customizedValues, missingKeys)
      result.push({ file: f, content, missingKeys })
    }
  }
  return result
}

export function forkYamProcess(args: string[], enableDebug: boolean, outputHandler: (msg: string) => void): ChildProcess {
  const forkEnv = JSON.parse(JSON.stringify(process.env))
  if (enableDebug) {
    forkEnv.DEBUG = true
  }
  // Use parent process colorize to avoid Linux color issue
  const stdoutStream = new Writable()
  stdoutStream._write = (chunk, _, callback) => {
    outputHandler(chunk.toString())
    callback()
  }
  const proc = fork(path.join(__dirname, '../bin/yam.js'), args, {
    env: forkEnv, silent: true
  })
  proc.stdout?.pipe(stdoutStream)
  proc.stderr?.pipe(stdoutStream)
  return proc
}

export type PlanApplyOption = {
  version?: string
  dir?: string
  dryRun?: boolean
  templateOutput?: boolean
  clusters?: string
  skipDiff?: boolean
  valuesFrom?: string
  set?: string
  waitSeconds?: string | boolean
  planFile?: string
  forceRestart?: boolean

  // inclusive/exclusive mode for bundle apply, 'up' command, separate by comma
  include?: string,
  exclude?: string,

  // plugin filters, when specified, only selected plugins will be enabled
  filter?: string

  // only used for 'yam template' command, extract yaml files to directory, defaults to './dist'
  output?: string
}

export type ValuesOption = {
  variables?: string | KV
  dir?: string
  valuesFrom?: string
  // env locator
  env?: string

  // designated filename to save values
  filename?: string
}

export interface OutputChannel {
  append: (value: string) => void
}

export function persistSettings(): void {
  if (!fs.existsSync(ENGINE_BASE_PATH)) {
    fs.mkdirSync(ENGINE_BASE_PATH)
  }
  fs.writeFileSync(confFile, yaml.dump(engineSettings))
}